import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DataExportPage({ token, user }) {
  const { language } = useLanguage();
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [masterFiles, setMasterFiles] = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const [masterSearch, setMasterSearch] = useState('');

  const isPlatformAdmin = user?.role === 'platform_admin';

  useEffect(() => {
    fetchExports();
    if (isPlatformAdmin) {
      fetchMasterFiles();
    }
  }, [isPlatformAdmin]);

  const fetchExports = async () => {
    try {
      const res = await fetch(`${API_URL}/api/data-export/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setExports(data.exports);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchMasterFiles = async () => {
    setMasterLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/data-export/master-db/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setMasterFiles(data.files || []);
    } catch (err) {
      console.error(err);
      setMasterFiles([]);
    }
    setMasterLoading(false);
  };

  const handleDownload = async (exp) => {
    setDownloading(exp.id);
    try {
      const res = await fetch(`${API_URL}${exp.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exp.id}_export.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
    setDownloading(null);
  };

  const handleMasterDownload = async (file) => {
    setDownloading(file.name);
    try {
      const res = await fetch(`${API_URL}/api/data-export/master-db/download/${encodeURIComponent(file.name)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Master download error:', err);
    }
    setDownloading(null);
  };

  const colors = ['from-blue-500 to-cyan-500', 'from-purple-500 to-violet-500', 'from-emerald-500 to-teal-500', 'from-orange-500 to-amber-500', 'from-rose-500 to-pink-500'];
  const filteredMasterFiles = masterFiles.filter((file) => file.label.toLowerCase().includes(masterSearch.toLowerCase()) || file.name.toLowerCase().includes(masterSearch.toLowerCase()));

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="data-export-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-800" style={{fontFamily: 'Outfit, sans-serif'}}>
          {language === 'hi' ? 'Data Export / डेटा एक्सपोर्ट' : 'Data Export'}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {isPlatformAdmin
            ? (language === 'hi' ? 'Platform और school data को secure तरीके से export करें' : 'Export platform and school data securely')
            : (language === 'hi' ? 'School records को CSV format में download करें' : 'Download school records in CSV format')}
        </p>
      </div>

      {isPlatformAdmin && (
        <div className="rounded-3xl border-2 border-orange-200 bg-white shadow-md overflow-hidden" data-testid="secure-master-db-section">
          <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-green-600 p-5 text-white">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="rounded-full bg-yellow-300 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-slate-900">Secure</span>
                  <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold">Platform Admin Only</span>
                </div>
                <h2 className="mt-2 text-2xl font-black" style={{fontFamily: 'Outfit, sans-serif'}}>
                  {language === 'hi' ? 'School Finder Master Database' : 'School Finder Master Database'}
                </h2>
                <p className="mt-1 text-sm text-white/90" data-testid="secure-master-db-copy">
                  {language === 'hi'
                    ? 'अब public /exports page हट चुका है। पूरा master DB केवल authenticated Platform Admin download कर सकता है।'
                    : 'The public /exports page is gone. Full master DB downloads are now available only to authenticated Platform Admins.'}
                </p>
              </div>

              <input
                value={masterSearch}
                onChange={(e) => setMasterSearch(e.target.value)}
                placeholder={language === 'hi' ? 'State या file नाम खोजें' : 'Search state or file name'}
                className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/70 focus:outline-none lg:max-w-xs"
                data-testid="master-db-search-input"
              />
            </div>
          </div>

          <div className="p-5">
            {masterLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-orange-500 border-t-transparent"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredMasterFiles.find((file) => file.is_all_india) && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="master-db-all-india-card">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-700">All India</p>
                        <h3 className="mt-1 text-xl font-black text-slate-900">
                          {filteredMasterFiles.find((file) => file.is_all_india)?.label}
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          {language === 'hi' ? '4.48L+ school records का secure export' : 'Secure export of 4.48L+ school records'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleMasterDownload(filteredMasterFiles.find((file) => file.is_all_india))}
                        className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                        data-testid="download-all-india-master-db-button"
                      >
                        {downloading === filteredMasterFiles.find((file) => file.is_all_india)?.name
                          ? (language === 'hi' ? 'डाउनलोड हो रहा है...' : 'Downloading...')
                          : (language === 'hi' ? 'All India file डाउनलोड करें' : 'Download All India file')}
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {filteredMasterFiles.map((file) => (
                    <div key={file.name} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`master-db-file-${file.name}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold text-slate-900">{file.label}</p>
                          <p className="mt-1 text-xs text-slate-500">{file.format} · {file.updated_at?.slice(0, 10)}</p>
                        </div>
                        <span className="rounded-full bg-orange-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-orange-700">
                          {file.format}
                        </span>
                      </div>

                      <button
                        onClick={() => handleMasterDownload(file)}
                        disabled={downloading === file.name}
                        className="mt-4 w-full rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:opacity-50"
                        data-testid={`download-master-db-${file.name}`}
                      >
                        {downloading === file.name
                          ? (language === 'hi' ? 'डाउनलोड हो रहा है...' : 'Downloading...')
                          : (language === 'hi' ? 'Secure Download' : 'Secure Download')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {exports.map((exp, i) => (
          <div key={exp.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all" data-testid={`export-card-${exp.id}`}>
            <div className={`bg-gradient-to-r ${colors[i % colors.length]} p-4`}>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-2">
                <i className={`fa-solid ${exp.icon} text-white text-xl`}></i>
              </div>
              <h3 className="text-white font-bold text-lg" style={{fontFamily: 'Outfit, sans-serif'}}>
                {language === 'hi' ? exp.label_hi : exp.label}
              </h3>
            </div>
            <div className="p-4">
              <p className="text-gray-500 text-sm mb-4">
                {language === 'hi' ? `${exp.label_hi} CSV format में export करें` : `Export ${exp.label} as CSV file`}
              </p>
              <button
                onClick={() => handleDownload(exp)}
                disabled={downloading === exp.id}
                className="w-full py-2.5 bg-gray-900 text-white rounded-xl font-medium text-sm hover:bg-gray-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid={`export-btn-${exp.id}`}
              >
                {downloading === exp.id ? (
                  <>
                    <i className="fa-solid fa-spinner animate-spin"></i>
                    {language === 'hi' ? 'डाउनलोड हो रहा है...' : 'Downloading...'}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-download"></i>
                    {language === 'hi' ? 'CSV डाउनलोड करें' : 'Download CSV'}
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
        <div className="flex items-start gap-3">
          <i className="fa-solid fa-circle-info text-blue-500 mt-0.5"></i>
          <div>
            <p className="text-sm text-blue-700 font-medium">
              {isPlatformAdmin
                ? (language === 'hi' ? 'Secure exports के बारे में' : 'About secure exports')
                : (language === 'hi' ? 'CSV Files के बारे में' : 'About CSV Files')}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {isPlatformAdmin
                ? (language === 'hi'
                    ? 'Master database downloads अब authenticated API के through ही मिलेंगे। Public index page हटा दिया गया है।'
                    : 'Master database downloads are now available only through authenticated APIs. The public index page has been removed.')
                : (language === 'hi'
                    ? 'CSV files को Microsoft Excel, Google Sheets, या किसी भी spreadsheet software में open कर सकते हैं। Hindi text correctly display होगा।'
                    : 'CSV files can be opened in Microsoft Excel, Google Sheets, or any spreadsheet software. Hindi text will display correctly.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
